import { type GridCellParams, DataGrid, ErrorState, NoRowsOverlay } from '@shared/components';
import { useGetAppRolesQuery, type OrderType } from '@user-management/graphql';
import { useNavigate } from '@user-management/router';
import { useMemo } from 'react';

import { useRoleListSearchParams } from '../../hooks';

import { columns } from './columns';
import type { AppRole } from './type';
import { mapAppRoleListData } from './utils';

export default function RoleList() {
  const navigate = useNavigate();

  const [
    { page, pageSize, search, sort },
    { changePageNumber, changePageSize, changeSortDirection },
  ] = useRoleListSearchParams();

  const {
    data,
    loading: isLoading,
    error,
  } = useGetAppRolesQuery({
    variables: {
      order: {
        field: 'name',
        type: sort.toUpperCase() as OrderType,
      },
      paging: {
        pageSize,
        currentPage: page,
      },
      search: {
        name: search,
      },
    },
  });

  const rows = useMemo(
    () => mapAppRoleListData(data?.getAppRoles?.roles ?? []),
    [data?.getAppRoles],
  );

  const handleCellClick = (params: GridCellParams<AppRole>) => {
    if (params.field !== 'moreAction') {
      navigate('/roles-and-permissions/:appRoleId/details', {
        params: {
          appRoleId: params.row.id,
        },
      });
    }
  };

  const rowsCount = data?.getAppRoles.total ?? 0;
  const shouldHideFooter = isLoading || rowsCount === 0;

  if (error) return <ErrorState />;

  return (
    <DataGrid
      loading={isLoading}
      columns={columns}
      hideFooter={shouldHideFooter}
      rows={rows}
      onCellClick={handleCellClick}
      disableColumnMenu
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      rowHeight={48}
      slotProps={{
        footer: {
          total: rowsCount,
          pagination: {
            page: page,
            count: rowsCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: 'name', sort }],
        },
      }}
      onSortModelChange={(sorting) => {
        const sortDirection = sorting[0]?.sort;
        if (sortDirection) {
          changeSortDirection(sortDirection);
        }
      }}
      sx={(theme) => ({
        '--DataGrid-overlayHeight': '412px',
        '.MuiDataGrid-cell': {
          padding: theme.spacing(3),
          paddingBottom: theme.spacing(2.75),
        },
        '.MuiDataGrid-columnHeader': {
          padding: theme.spacing(3),
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderColor: theme.palette.grey[300],
        },
        backgroundColor: theme.palette.common.white,
        '& .MuiDataGrid-row.Mui-selected': {
          backgroundColor: theme.palette.grey[100],
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: theme.palette.grey[100],
        },
      })}
    />
  );
}
